import React, { useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import {
  Box, IconButton, Tooltip, Divider, CircularProgress, Popover
} from '@mui/material';
import {
  FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered,
  FormatQuote, Image as ImageIcon, VideoFile, Link as LinkIcon,
  Undo, Redo, Title, FormatAlignLeft, FormatAlignRight,
  FormatAlignCenter, FormatAlignJustify, FormatColorText,
  FormatColorFill
} from '@mui/icons-material';
import { snackbar, successMessage } from '../utils/showMessage';

// Custom Video Node
const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      style: { default: 'max-width:100%;border-radius:8px;' },
    };
  },
  parseHTML() {
    return [{ tag: 'video' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes, { controls: true, style: 'max-width:100%;border-radius:8px;' })];
  },
});

const TEXT_COLORS = [
  '#000000', '#333333', '#666666', '#999999', '#cccccc',
  '#e53935', '#d81b60', '#8e24aa', '#3949ab', '#1e88e5',
  '#00897b', '#43a047', '#fb8c00', '#f4511e', '#6d4c41',
];

const BG_COLORS = [
  'transparent',
  '#fff176', '#ffcc80', '#ef9a9a', '#f48fb1',
  '#ce93d8', '#9fa8da', '#81d4fa', '#80cbc4',
  '#a5d6a7', '#e6ee9c', '#ffab91', '#bcaaa4',
];

const ToolbarButton = ({ onClick, active, title, children, disabled }) => (
  <Tooltip title={title} placement="top">
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderRadius: '6px',
          p: '4px',
          bgcolor: active ? 'primary.main' : 'transparent',
          color: active ? '#fff' : 'text.secondary',
          '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const ColorPicker = ({ title, icon, colors, onSelect, activeColor }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip title={title} placement="top">
        <span>
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{
              borderRadius: '6px',
              p: '4px',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
              position: 'relative',
            }}
          >
            {React.cloneElement(icon, { fontSize: 'small' })}
            {/* color strip under icon */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 2,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 14,
                height: 3,
                borderRadius: 1,
                bgcolor: activeColor && activeColor !== 'transparent' ? activeColor : 'text.disabled',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, borderRadius: 2 } }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: 160 }}>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => { onSelect(color); setAnchor(null); }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: 1,
                bgcolor: color === 'transparent' ? 'transparent' : color,
                border: color === activeColor
                  ? '2px solid #1976d2'
                  : color === 'transparent'
                  ? '1px dashed #aaa'
                  : '1px solid rgba(0,0,0,0.12)',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.15)', transition: 'transform 0.1s' },
              }}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

// onUpload: async (file) => url string
const RichTextEditor = ({ value, onChange, label, onUpload }) => {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false }),
      Video,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        style: 'outline: none; min-height: 200px; padding: 12px; font-family: inherit; font-size: 14px; line-height: 1.8;',
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (open for edit)
  React.useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value === '' ? value : undefined]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !editor || !onUpload) return;
    e.target.value = '';
    setUploading(true);
    try {
      const url = await onUpload(file);
      editor.chain().focus('end').setImage({ src: url }).run();
      successMessage('تصویر با موفقیت آپلود شد');
    } catch {
      snackbar('خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  }, [editor, onUpload]);

  const handleVideoUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !editor || !onUpload) return;
    e.target.value = '';
    setUploading(true);
    try {
      const url = await onUpload(file);
      editor.chain().focus('end').insertContent({ type: 'video', attrs: { src: url } }).run();
      successMessage('ویدیو با موفقیت آپلود شد');
    } catch {
      snackbar('خطا در آپلود ویدیو');
    } finally {
      setUploading(false);
    }
  }, [editor, onUpload]);

  const handleAddLink = useCallback(() => {
    const url = window.prompt('آدرس لینک را وارد کنید:');
    if (!url || !editor) return;
    editor.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const activeTextColor = editor.getAttributes('textStyle')?.color;
  const activeHighlight = editor.getAttributes('highlight')?.color;

  return (
    <Box
      sx={{
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: '#fff',
        mb: 2,
      }}
    >
      {label && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0 }}>
          <Box component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>{label}</Box>
        </Box>
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          p: 1,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          bgcolor: '#fafafa',
          alignItems: 'center',
        }}
      >
        {/* Heading */}
        <ToolbarButton
          title="عنوان"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          <Title fontSize="small" />
        </ToolbarButton>

        {/* Bold / Italic */}
        <ToolbarButton
          title="پررنگ"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <FormatBold fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="کج"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <FormatItalic fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="نقل‌قول"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          <FormatQuote fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Alignment + Direction */}
        <ToolbarButton
          title="راست‌چین (RTL)"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
        >
          <FormatAlignRight fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="چپ‌چین (LTR)"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
        >
          <FormatAlignLeft fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="وسط‌چین"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
        >
          <FormatAlignCenter fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="تراز دو طرف"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
        >
          <FormatAlignJustify fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <ToolbarButton
          title="لیست نقطه‌ای"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <FormatListBulleted fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="لیست عددی"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <FormatListNumbered fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Colors */}
        <ColorPicker
          title="رنگ متن"
          icon={<FormatColorText />}
          colors={TEXT_COLORS}
          activeColor={activeTextColor}
          onSelect={(color) =>
            editor.chain().focus().setColor(color).run()
          }
        />
        <ColorPicker
          title="رنگ پس‌زمینه متن"
          icon={<FormatColorFill />}
          colors={BG_COLORS}
          activeColor={activeHighlight}
          onSelect={(color) =>
            color === 'transparent'
              ? editor.chain().focus().unsetHighlight().run()
              : editor.chain().focus().setHighlight({ color }).run()
          }
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link / Media */}
        <ToolbarButton title="لینک" onClick={handleAddLink} active={editor.isActive('link')}>
          <LinkIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="آپلود تصویر"
          onClick={() => imageInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={16} /> : <ImageIcon fontSize="small" />}
        </ToolbarButton>
        <ToolbarButton
          title="آپلود ویدیو"
          onClick={() => videoInputRef.current?.click()}
          disabled={uploading}
        >
          <VideoFile fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Undo / Redo */}
        <ToolbarButton title="برگشت" onClick={() => editor.chain().focus().undo().run()}>
          <Undo fontSize="small" />
        </ToolbarButton>
        <ToolbarButton title="جلو" onClick={() => editor.chain().focus().redo().run()}>
          <Redo fontSize="small" />
        </ToolbarButton>
      </Box>

      {/* Editor area */}
      <Box
        sx={{
          '& .tiptap': {
            minHeight: '200px',
            outline: 'none',
            '& img': { maxWidth: '100%', borderRadius: '8px', margin: '8px 0', display: 'block' },
            '& video': { maxWidth: '100%', borderRadius: '8px', margin: '8px 0', display: 'block' },
            '& blockquote': {
              borderRight: '3px solid #ccc',
              paddingRight: '12px',
              color: '#666',
              margin: '8px 0',
            },
            '& ul, & ol': { paddingRight: '20px' },
            '& h2': { fontSize: '1.3rem', fontWeight: 'bold', margin: '8px 0' },
            '& mark': { borderRadius: '3px', padding: '0 2px' },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <input ref={imageInputRef} type="file" hidden accept="image/*" onChange={handleImageUpload} />
      <input ref={videoInputRef} type="file" hidden accept="video/*" onChange={handleVideoUpload} />
    </Box>
  );
};

export default RichTextEditor;
